import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import useSmartyUSLookup from '../hooks/useSmartyUSLookup';
import useUSAddressValidation from '../hooks/useUSAddressValidation';
import useAddressFieldLabels from '../hooks/useAddressFieldLabels';
import useAddressForm from '../hooks/useAddressForm';
import useInitialAddressVerification from '../hooks/useInitialAddressVerification';
import useAddressMapModal from '../hooks/useAddressMapModal';
import { buildUSVerifiedAddressMapping } from '../utils/addressMapping';
import { wrapAddress1ToAddress2 } from '../utils/addressWrapper';
import { buildAddressStatus, getRequiredFieldsFromAddressForm } from '../utils/addressStatus';
import {
  getIncomingVerificationStatus,
  getManualEditVerificationStatus,
  withAddressVerification,
  withManualAddressVerificationEdit
} from '../utils/addressVerification';
import { isValidEmail, normalizeAddressFieldValue, normalizePostalCodeValue, filterAllowedCharacters } from '../utils/validators';
import { addressPropType, formDefinitionPropType, labelStringsPropType } from '../utils/componentPropTypes';
import LocationPinIcon from './LocationPinIcon';
import { NsButton, NsInputField } from '@nuskin/foundation-ui-components';

const stripGeocode = (address = {}) => {
  const nextAddress = { ...address };
  delete nextAddress.latitude;
  delete nextAddress.longitude;
  return nextAddress;
};

const FIELD_TEST_IDS = {
  address1: 'qa-address-1',
  address2: 'qa-address-2',
  district: 'qa-address-district',
  region: 'qa-address-region',
  city: 'qa-address-city',
  postalCode: 'qa-address-postal-code'
};

const FIELD_IDS = {
  address1: 'address-line-1',
  address2: 'address-line-2',
  district: 'address-district',
  region: 'address-region',
  city: 'address-city',
  postalCode: 'address-postal-code'
};

const getFieldTestId = (field) => FIELD_TEST_IDS[field] || `qa-address-${field}`;
const getFieldId = (field) => FIELD_IDS[field] || `address-${field}`;
const LOOKUP_LIST_BUTTON_SELECTOR = '.ns-address-lookup__list-button';

const SmartyUSResults = ({
  onSelect,
  initialAddress = null,
  requireValidation = true,
  readOnlyView = false,
  showManualValidateButton = false,
  validationRequestId = 0,
  language = 'en',
  strings = null,
  formDefinition = null,
  deferFormLookup = false
}) => {
  const [mode, setMode] = useState('autocomplete');
  const [query, setQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedUSAddress, setSelectedUSAddress] = useState(null);
  const [formData, setFormData] = useState(initialAddress || {});
  const [verificationStatus, setVerificationStatus] = useState(getIncomingVerificationStatus(initialAddress));
  const [validationTrigger, setValidationTrigger] = useState(0);
  const [validationTriggerSource, setValidationTriggerSource] = useState(null);
  const [showValidationSuggestions, setShowValidationSuggestions] = useState(false);
  const [mapAddressLines, setMapAddressLines] = useState([]);
  const [hasPendingLookupDraft, setHasPendingLookupDraft] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllRequiredErrors, setShowAllRequiredErrors] = useState(false);
  const [lookupServiceError, setLookupServiceError] = useState(null);
  const [validationServiceError, setValidationServiceError] = useState(null);
  const shouldSearchRef = useRef(true);
  const fieldTimerRef = useRef(null);
  const componentRef = useRef(null);
  const suggestionsListRef = useRef(null);
  const lastCommittedAddressRef = useRef(initialAddress || {});
  const lookupDraftOriginalRef = useRef(initialAddress || {});
  const appliedSelectionKeyRef = useRef(null);
  const mapValidationStartedRef = useRef(false);
  const autoApplyValidationStartedRef = useRef(false);
  const hasInitializedAddressRef = useRef(false);

  useEffect(() => {
    if (!initialAddress || hasInitializedAddressRef.current) {
      return;
    }

    hasInitializedAddressRef.current = true;
    setFormData(initialAddress);
    setVerificationStatus(getIncomingVerificationStatus(initialAddress));
    shouldSearchRef.current = false;
    lastCommittedAddressRef.current = initialAddress;
    lookupDraftOriginalRef.current = initialAddress;
    setTouchedFields({});
    setShowAllRequiredErrors(false);
    setLookupServiceError(null);
    setValidationServiceError(null);
    setMapAddressLines([]);
  }, [initialAddress]);

  useEffect(() => {
    return () => {
      if (fieldTimerRef.current) {
        clearTimeout(fieldTimerRef.current);
      }
    };
  }, []);

  const isAutocompleteMode = mode === 'autocomplete';
  const { labels: fieldLabels, isLoading: areLabelsLoading } = useAddressFieldLabels('US', language, strings);
  const shouldLoadAddressForm = !formDefinition && !deferFormLookup;
  const { addressForm: loadedAddressForm, isLoading: isFormLoading } = useAddressForm('US', shouldLoadAddressForm);
  const providedAddressForm = formDefinition?.addressElements?.length ? formDefinition : null;
  const resolvedAddressForm = providedAddressForm || loadedAddressForm;
  const allowValidation = requireValidation && !readOnlyView;
  const lookupField = resolvedAddressForm?.lookupField || 'address1';
  const requiredFields = getRequiredFieldsFromAddressForm(resolvedAddressForm);
  const hasEmailField = (resolvedAddressForm?.addressElements || []).some((field) => field.field === 'email');
  const hasMapCoordinates = (
    formData?.latitude !== undefined && formData?.longitude !== undefined
  );
  const hasLookupValue = Boolean(String(formData?.[lookupField] || '').trim());
  const canAttemptMap = Boolean(
    (!isAutocompleteMode && hasLookupValue) ||
    (isAutocompleteMode && ['autocomplete', 'verified'].includes(verificationStatus) && hasLookupValue)
  );
  const showMapAction = !readOnlyView && (hasMapCoordinates || canAttemptMap);
  const mapInfoLines = (mapAddressLines.length ? mapAddressLines : [
    formData.address1,
    formData.address2,
    [formData.city, formData.region, formData.postalCode].filter(Boolean).join(' ')
  ]).filter(Boolean);
  const {
    mapContainerRef,
    pendingMapOpen,
    setPendingMapOpen,
    setShowMapModal,
    showMapModal
  } = useAddressMapModal({
    formData,
    infoLines: mapInfoLines,
    hasMapCoordinates,
    openInMapsLabel: fieldLabels?.openInMaps,
    shouldPrepareMap: showMapAction
  });

  const { suggestions, loading, error, rateLimited } = useSmartyUSLookup(
    !readOnlyView && shouldSearchRef.current && isAutocompleteMode ? query : '',
    selectedUSAddress,
    500
  );
  const {
    suggestions: validationSuggestions,
    loading: validationLoading,
    error: validationError,
    rateLimited: validationRateLimited
  } = useUSAddressValidation(formData, allowValidation ? validationTrigger : 0);
  const lookupValue = formData[lookupField];
  const lookupErrorMessage = lookupServiceError?.rateLimited ?
    (fieldLabels?.rateLimited || 'Too many requests. Please wait a moment and try again.') :
    (fieldLabels?.lookupError || 'We could not look up that address right now.');
  const validationErrorMessage = validationServiceError?.rateLimited ?
    (fieldLabels?.rateLimited || 'Too many requests. Please wait a moment and try again.') :
    (fieldLabels?.validationError || 'We could not validate that address right now.');

  useEffect(() => {
    if (!validationRequestId) {
      return;
    }

    setShowAllRequiredErrors(true);
    onSelect?.({
      type: 'validation-request',
      address: formData,
      status: buildAddressStatus({
        countryCode: 'US',
        addressForm: resolvedAddressForm,
        formData,
        verificationStatus,
        mode,
        requireValidation: allowValidation
      })
    });
  }, [allowValidation, validationRequestId]);

  useEffect(() => {
    if (!error) {
      setLookupServiceError(null);
      return;
    }

    setLookupServiceError({
      rateLimited,
      rawMessage: error
    });
  }, [error, rateLimited]);

  useEffect(() => {
    if (!validationError) {
      setValidationServiceError(null);
      return;
    }

    setValidationServiceError({
      rateLimited: validationRateLimited,
      rawMessage: validationError
    });
    setPendingMapOpen(false);
    mapValidationStartedRef.current = false;
  }, [validationError, validationRateLimited]);

  useEffect(() => {
    if (!lookupServiceError) {
      return;
    }

    onSelect?.({
      type: 'lookup-error',
      address: formData,
      status: buildAddressStatus({
        countryCode: 'US',
        addressForm: resolvedAddressForm,
        formData,
        verificationStatus,
        mode,
        requireValidation: allowValidation
      }),
      error: {
        kind: lookupServiceError.rateLimited ? 'rate-limited' : 'api',
        source: 'autocomplete',
        message: lookupErrorMessage
      }
    });
  }, [lookupServiceError]);

  useEffect(() => {
    if (!validationServiceError) {
      return;
    }

    onSelect?.({
      type: 'validation-error',
      address: formData,
      status: buildAddressStatus({
        countryCode: 'US',
        addressForm: resolvedAddressForm,
        formData,
        verificationStatus,
        mode,
        requireValidation: allowValidation
      }),
      error: {
        kind: validationServiceError.rateLimited ? 'rate-limited' : 'api',
        source: 'validation',
        message: validationErrorMessage
      }
    });
  }, [validationServiceError]);

  const clearServiceErrors = ({ lookup = true, validation = true } = {}) => {
    if (lookup) {
      setLookupServiceError(null);
    }
    if (validation) {
      setValidationServiceError(null);
    }
  };

  const stripUnusedEmail = (address = {}) => {
    if (hasEmailField) {
      return address;
    }

    const nextAddress = { ...address };
    delete nextAddress.email;
    return nextAddress;
  };

  const buildTrackedAddress = (address = {}, status = 'unverified', source = null) => (
    stripUnusedEmail(withAddressVerification(address, status, source))
  );

  const isAcceptableInitialValidationResult = (validatedAddress) => (
    Boolean(validatedAddress?.isDeliverable)
  );

  const getBestInitialValidationResult = (suggestions = []) => (
    suggestions.find(isAcceptableInitialValidationResult) || null
  );

  useInitialAddressVerification({
    initialAddress,
    countryCode: 'US',
    lookupField,
    requireValidation: allowValidation,
    resolvedAddressForm,
    setValidationServiceError,
    setShowValidationSuggestions,
    setValidationTriggerSource,
    setValidationTrigger
  });

  useEffect(() => {
    if (validationTrigger > 0 && ['autocomplete', 'map', 'initial'].includes(validationTriggerSource)) {
      autoApplyValidationStartedRef.current = false;
    }
  }, [validationTrigger, validationTriggerSource]);

  useEffect(() => {
    if (validationLoading && validationTriggerSource === 'map') {
      mapValidationStartedRef.current = true;
    }
    if (validationLoading && ['autocomplete', 'map', 'initial'].includes(validationTriggerSource)) {
      autoApplyValidationStartedRef.current = true;
    }
  }, [validationLoading, validationTriggerSource]);

  useEffect(() => {
    if (
      validationTriggerSource === 'map' &&
      showValidationSuggestions &&
      !validationLoading &&
      validationSuggestions.length === 0 &&
      !validationServiceError &&
      mapValidationStartedRef.current
    ) {
      setPendingMapOpen(false);
      mapValidationStartedRef.current = false;
    }
  }, [validationLoading, validationSuggestions, validationTriggerSource, showValidationSuggestions, validationServiceError]);

  useEffect(() => {
    if (lookupValue !== undefined && shouldSearchRef.current && !selectedAddress) {
      setQuery(lookupValue);
    }
  }, [lookupValue, selectedAddress]);

  useEffect(() => {
    const shouldShowValidationResults =
      allowValidation &&
      showValidationSuggestions &&
      validationSuggestions.length > 0 &&
      !validationLoading &&
      (!isAutocompleteMode || validationTriggerSource === 'map');

    if (!shouldShowValidationResults) {
      return;
    }

    const suggestionButtons = getVisibleSuggestionButtons();

    if (suggestionButtons.length === 0 || suggestionButtons.includes(document.activeElement)) {
      return;
    }

    suggestionButtons[0]?.focus();
  }, [
    isAutocompleteMode,
    allowValidation,
    showValidationSuggestions,
    validationLoading,
    validationSuggestions,
    validationTriggerSource
  ]);

  useEffect(() => {
    const shouldRefocusLookupField =
      isAutocompleteMode &&
      selectedUSAddress &&
      suggestions.length > 0 &&
      !selectedAddress &&
      !loading;

    if (!shouldRefocusLookupField) {
      return;
    }

    getLookupInputElement()?.focus();
  }, [isAutocompleteMode, selectedUSAddress, suggestions, selectedAddress, loading]);

  useEffect(() => {
    if (!hasPendingLookupDraft || suggestions.length === 0) {
      return undefined;
    }

    const handleDocumentMouseDown = (event) => {
      const component = componentRef.current;
      if (component?.contains(event.target)) {
        return;
      }

      const suggestionsList = suggestionsListRef.current;
      if (suggestionsList?.contains(event.target) === false) {
        shouldSearchRef.current = false;
        setQuery('');
        setSelectedAddress(null);
        setSelectedUSAddress(null);
        setShowValidationSuggestions(false);
        setShowMapModal(false);
        setPendingMapOpen(false);
        mapValidationStartedRef.current = false;
        clearServiceErrors();
        setFormData(lookupDraftOriginalRef.current);
        setMapAddressLines([]);
        setHasPendingLookupDraft(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('pointerdown', handleDocumentMouseDown);
    };
  }, [hasPendingLookupDraft, suggestions]);

  useEffect(() => {
    if (selectedAddress) {
      const selectionKey = selectedAddress.street_line ?
        `${selectedAddress.street_line}|${selectedAddress.secondary || ''}|${selectedAddress.zipcode || ''}` :
        JSON.stringify(selectedAddress);
      if (appliedSelectionKeyRef.current === selectionKey) {
        return;
      }
      appliedSelectionKeyRef.current = selectionKey;

      const config = resolvedAddressForm.addressElements || [];

      let mapped = {
        address1: selectedAddress.street_line,
        address2: selectedAddress.secondary || '',
        city: selectedAddress.city,
        region: selectedAddress.state,
        postalCode: selectedAddress.zipcode
      };

      // Apply address wrapping if enabled
      if (resolvedAddressForm.wrapAddress1ToAddress2) {
        const address1Field = config.find(f => f.field === 'address1');
        if (address1Field?.maxLength) {
          const wrapped = wrapAddress1ToAddress2(mapped.address1, mapped.address2, address1Field.maxLength);
          mapped.address1 = wrapped.address1;
          mapped.address2 = wrapped.address2;
        }
      }

      const updatedFormData = {
        ...stripGeocode(formData),
        ...mapped,
        email: formData.email
      };
      const normalizedFormData = buildTrackedAddress(updatedFormData, 'autocomplete', 'smarty-us');
      setFormData(normalizedFormData);
      setMapAddressLines([]);
      lastCommittedAddressRef.current = normalizedFormData;
      lookupDraftOriginalRef.current = normalizedFormData;
      setHasPendingLookupDraft(false);
      clearServiceErrors();
      const currentVerificationStatus = 'autocomplete';
      setVerificationStatus(currentVerificationStatus);
      onSelect?.({
        type: 'autocomplete',
        address: normalizedFormData,
        status: buildAddressStatus({
          countryCode: 'US',
          addressForm: resolvedAddressForm,
          formData: normalizedFormData,
          verificationStatus: currentVerificationStatus,
          mode,
          requireValidation: allowValidation
        })
      });
    }
  }, [selectedAddress]);

  const handleInputChange = (e) => {
    const value = normalizeAddressFieldValue(lookupField, e.target.value);
    shouldSearchRef.current = true;
    appliedSelectionKeyRef.current = null;
    setSelectedAddress(null);
    setSelectedUSAddress(null);
    setShowValidationSuggestions(false);
    setShowMapModal(false);
    setPendingMapOpen(false);
    mapValidationStartedRef.current = false;
    setMapAddressLines([]);
    setValidationTriggerSource(null);
    clearServiceErrors();
    if (!hasPendingLookupDraft) {
      lookupDraftOriginalRef.current = formData;
      setHasPendingLookupDraft(true);
    }
    const updatedLookupData = stripUnusedEmail({ ...stripGeocode(formData), [lookupField]: value });
    setFormData(buildTrackedAddress(updatedLookupData, 'unverified'));
    setVerificationStatus('unverified');
  };

  const isFieldRequired = (field) => requiredFields.includes(field);
  const isFieldBlank = (field) => String(formData[field] || '').trim() === '';
  const hasInvalidPostalCodeFormat = (field) => {
    if (field !== 'postalCode') {
      return false;
    }
    const value = String(formData.postalCode || '').trim();
    if (!value || !resolvedAddressForm.postalCodeRegex) {
      return false;
    }
    return !resolvedAddressForm.postalCodeRegex.test(normalizePostalCodeValue(value));
  };
  const hasInvalidEmailFormat = (field) => {
    if (field !== 'email') {
      return false;
    }
    const value = String(formData.email || '').trim();
    if (!value) {
      return false;
    }
    return !isValidEmail(value);
  };
  const shouldShowFieldError = (field) => {
    const shouldShowValidation = Boolean(showAllRequiredErrors || touchedFields[field]);
    if (!shouldShowValidation) {
      return false;
    }
    const requiredError = isFieldRequired(field) && isFieldBlank(field);
    return requiredError || hasInvalidPostalCodeFormat(field) || hasInvalidEmailFormat(field);
  };

  const handleFieldBlur = (field) => {
    if (!isFieldRequired(field) && field !== 'email' && field !== 'postalCode') {
      return;
    }
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const getFieldErrorMessage = (field) => {
    if (!shouldShowFieldError(field)) {
      return '';
    }
    if (isFieldRequired(field) && isFieldBlank(field)) {
      return areLabelsLoading ? '' : fieldLabels?.fieldRequired;
    }
    if (hasInvalidPostalCodeFormat(field)) {
      return areLabelsLoading ? '' : fieldLabels?.invalidPostalCode;
    }
    if (hasInvalidEmailFormat(field)) {
      return areLabelsLoading ? '' : fieldLabels?.invalidEmail;
    }
    return '';
  };

  const hasVisibleFieldError = (field) => shouldShowFieldError(field);

  const handleEditableFieldChange = (field, value) => {
    const normalizedValue = normalizeAddressFieldValue(field, value);
    if (field === lookupField) {
      setShowValidationSuggestions(false);
    }
    clearServiceErrors();
    setShowMapModal(false);
    setPendingMapOpen(false);
    mapValidationStartedRef.current = false;
    setMapAddressLines([]);

    const requiredFieldsForVerification = resolvedAddressForm?.requiredFields || requiredFields;
    const nextVerificationStatus = getManualEditVerificationStatus(
      verificationStatus,
      field,
      lookupField,
      requiredFieldsForVerification
    );
    const updatedFormData = withManualAddressVerificationEdit(
      stripUnusedEmail({ ...stripGeocode(formData), [field]: normalizedValue }),
      field,
      nextVerificationStatus
    );
    setFormData(updatedFormData);
    setVerificationStatus(nextVerificationStatus);

    if (fieldTimerRef.current) {
      clearTimeout(fieldTimerRef.current);
    }

    fieldTimerRef.current = setTimeout(() => {
      const payload = {
        type: 'field-change',
        field,
        value: normalizedValue,
        address: updatedFormData,
        status: buildAddressStatus({
          countryCode: 'US',
          addressForm: resolvedAddressForm,
          formData: updatedFormData,
          verificationStatus: nextVerificationStatus,
          mode,
          requireValidation: allowValidation
        })
      };

      if (hasEmailField && field === 'email' && value) {
        payload.isValidEmail = isValidEmail(normalizedValue);
      }

      onSelect?.(payload);
    }, 500);
  };

  const handleSelect = (address) => {
    clearServiceErrors({ lookup: true, validation: false });
    if (address.entries > 1) {
      shouldSearchRef.current = true;
      appliedSelectionKeyRef.current = null;
      setSelectedUSAddress(address);
      const baseAddress = `${address.street_line} ${address.secondary}`.trim();
      setQuery(baseAddress);
    } else {
      shouldSearchRef.current = false;
      appliedSelectionKeyRef.current = null;
      setSelectedUSAddress(null);
      setSelectedAddress(address);
      setQuery('');
    }
  };

  const handleValidationSelect = (validatedAddress) => {
    clearServiceErrors({ lookup: false, validation: true });
    const config = resolvedAddressForm.addressElements || [];
    const {
      mapped: verifiedMapped,
      mapAddressLines: nextMapAddressLines
    } = buildUSVerifiedAddressMapping(validatedAddress);

    let mapped = { ...verifiedMapped };

    if (resolvedAddressForm.wrapAddress1ToAddress2) {
      const address1Field = config.find(f => f.field === 'address1');
      if (address1Field?.maxLength) {
        const wrapped = wrapAddress1ToAddress2(mapped.address1, mapped.address2, address1Field.maxLength);
        mapped.address1 = wrapped.address1;
        mapped.address2 = wrapped.address2;
      }
    }

    const updatedFormData = {
      ...stripGeocode(formData),
      ...mapped,
      email: formData.email,
      latitude: validatedAddress.metadata?.latitude,
      longitude: validatedAddress.metadata?.longitude
    };
    const normalizedFormData = buildTrackedAddress(updatedFormData, 'verified', 'smarty-us');
    setShowValidationSuggestions(false);
    setValidationTriggerSource(null);
    setMapAddressLines(nextMapAddressLines);
    setFormData(normalizedFormData);
    lastCommittedAddressRef.current = normalizedFormData;
    lookupDraftOriginalRef.current = normalizedFormData;
    setHasPendingLookupDraft(false);
    const currentVerificationStatus = 'verified';
    setVerificationStatus(currentVerificationStatus);
    autoApplyValidationStartedRef.current = false;

    if (pendingMapOpen) {
      setPendingMapOpen(false);
      mapValidationStartedRef.current = false;
      setShowMapModal(true);
    }

    onSelect?.({
      type: 'validation',
      address: normalizedFormData,
      verification: validatedAddress.analysis,
      isDeliverable: validatedAddress.isDeliverable,
      status: buildAddressStatus({
        countryCode: 'US',
        addressForm: resolvedAddressForm,
        formData: normalizedFormData,
        verificationStatus: currentVerificationStatus,
        mode,
        requireValidation: allowValidation
      })
    });
  };

  const handleValidate = () => {
    if (!allowValidation) {
      return;
    }
    clearServiceErrors({ lookup: false, validation: true });
    setShowValidationSuggestions(true);
    setValidationTriggerSource('manual');
    setValidationTrigger(prev => prev + 1);
  };

  const handleOpenMap = () => {
    if (hasMapCoordinates) {
      setShowMapModal(true);
      return;
    }

    if (!canAttemptMap || validationLoading) {
      return;
    }

    clearServiceErrors({ lookup: false, validation: true });
    mapValidationStartedRef.current = false;
    setPendingMapOpen(true);
    setShowValidationSuggestions(true);
    setValidationTriggerSource('map');
    setValidationTrigger((prev) => prev + 1);
  };

  const getFieldKey = (fieldConfig) => fieldConfig.field;

  const getSuggestionKey = (suggestion) => [
    suggestion.street_line,
    suggestion.secondary,
    suggestion.city,
    suggestion.state,
    suggestion.zipcode,
    suggestion.entries
  ].filter((value) => value !== undefined && value !== null).join('|');

  const getValidationSuggestionKey = (suggestion) => [
    suggestion.delivery_line_1,
    suggestion.delivery_line_2,
    suggestion.last_line,
    suggestion.dpvMatchCode
  ].filter((value) => value !== undefined && value !== null).join('|');

  const getValidationSummaryTitle = (suggestion) => {
    const code = suggestion.validationSummary?.code;

    switch (code) {
      case 'verified':
        return fieldLabels?.usVerified || suggestion.validationSummary?.title || 'Verified';
      case 'missing-secondary':
        return fieldLabels?.usMissingSecondaryTitle || suggestion.validationSummary?.title || 'Apartment or suite needed';
      case 'invalid-secondary-required':
        return fieldLabels?.usInvalidSecondaryRequiredTitle || suggestion.validationSummary?.title || 'Apartment or suite not recognized';
      case 'invalid-secondary-optional':
        return fieldLabels?.usInvalidSecondaryOptionalTitle || suggestion.validationSummary?.title || 'Unit not recognized';
      case 'vacant':
        return fieldLabels?.usVacantTitle || suggestion.validationSummary?.title || 'Verified, but marked vacant';
      case 'no-stat':
        return fieldLabels?.usNoStatTitle || suggestion.validationSummary?.title || 'Verified with caution';
      case 'not-confirmed':
        return fieldLabels?.usNotConfirmedTitle || suggestion.validationSummary?.title || 'Not verified';
      default:
        return suggestion.validationSummary?.title || 'Candidate';
    }
  };

  const getValidationSummaryMessage = (suggestion) => {
    const code = suggestion.validationSummary?.code;

    switch (code) {
      case 'missing-secondary':
        return fieldLabels?.usMissingSecondaryMessage || suggestion.validationSummary?.message || '';
      case 'invalid-secondary-required':
        return fieldLabels?.usInvalidSecondaryRequiredMessage || suggestion.validationSummary?.message || '';
      case 'invalid-secondary-optional':
        return fieldLabels?.usInvalidSecondaryOptionalMessage || suggestion.validationSummary?.message || '';
      case 'vacant':
        return fieldLabels?.usVacantMessage || suggestion.validationSummary?.message || '';
      case 'no-stat':
        return fieldLabels?.usNoStatMessage || suggestion.validationSummary?.message || '';
      case 'not-confirmed':
        return fieldLabels?.usNotConfirmedMessage || suggestion.validationSummary?.message || '';
      case 'verified':
        return fieldLabels?.usVerifiedMessage || suggestion.validationSummary?.message || '';
      default:
        return suggestion.validationSummary?.message || '';
    }
  };

  const renderLookupActions = () => (
    <div className="ns-address-lookup__actions">
      {showMapAction && (
        <button
          type="button"
          onClick={handleOpenMap}
          className="ns-address-lookup__toggle ns-address-lookup__toggle--icon"
          title={fieldLabels?.openInMaps}
          aria-label={fieldLabels?.openInMaps}
        >
          <LocationPinIcon className="ns-address-lookup__icon" />
        </button>
      )}
      {!isAutocompleteMode && allowValidation && showManualValidateButton && (
        <NsButton
          type="button"
          onClick={handleValidate}
          disabled={validationLoading || !String(formData[lookupField] || '').trim()}
          variant="contained"
          color="primary"
          size="small"
        >
          {validationLoading ? 'Validating...' : 'Validate Address'}
        </NsButton>
      )}
      <button
        type="button"
        onClick={() => {
          setShowValidationSuggestions(false);
          setShowMapModal(false);
          setPendingMapOpen(false);
          mapValidationStartedRef.current = false;
          setValidationTriggerSource(null);
          clearServiceErrors();
          setMode(mode === 'autocomplete' ? 'manual' : 'autocomplete');
        }}
        className="ns-address-lookup__toggle"
      >
        {mode === 'autocomplete' ?
          fieldLabels?.switchToManualEntry :
          fieldLabels?.switchToAutocomplete}
      </button>
    </div>
  );

  const getLookupInputElement = () => (
    typeof document !== 'undefined' ? document.getElementById(getFieldId(lookupField)) : null
  );

  const getVisibleSuggestionButtons = () => (
    Array.from(componentRef.current?.querySelectorAll(LOOKUP_LIST_BUTTON_SELECTOR) || [])
  );

  const handleLookupInputKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    const suggestionButtons = getVisibleSuggestionButtons();

    if (suggestionButtons.length === 0) {
      return;
    }

    event.preventDefault();

    const targetButton = event.key === 'ArrowUp' ?
      suggestionButtons[suggestionButtons.length - 1] :
      suggestionButtons[0];

    targetButton?.focus();
  };

  const handleSuggestionButtonKeyDown = (event, index) => {
    const suggestionButtons = getVisibleSuggestionButtons();

    if (suggestionButtons.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (index === suggestionButtons.length - 1) {
        getLookupInputElement()?.focus();
        return;
      }

      suggestionButtons[index + 1]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (index === 0) {
        getLookupInputElement()?.focus();
        return;
      }

      suggestionButtons[index - 1]?.focus();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      getLookupInputElement()?.focus();
    }
  };

  const renderLookupLists = (isLookupField) => {
    if (readOnlyView) {
      return null;
    }

    const shouldShowValidationResults =
      allowValidation &&
      showValidationSuggestions &&
      (!isAutocompleteMode || validationTriggerSource === 'map');
    const shouldShowNoResults =
      shouldShowValidationResults &&
      !validationLoading &&
      validationSuggestions.length === 0 &&
      !validationServiceError &&
      (validationTriggerSource !== 'map' || mapValidationStartedRef.current);

    if (!isLookupField) {
      return null;
    }

    if (loading && isAutocompleteMode) {
      return <div className="ns-address-lookup__spinner" />;
    }

    if (suggestions.length > 0 && !selectedAddress && isAutocompleteMode) {
      return (
        <ul
          ref={suggestionsListRef}
          className="ns-address-lookup__list ns-address-lookup__list--suggestions">
          {suggestions.map((suggestion, index) => (
            <li
              key={getSuggestionKey(suggestion)}
              className={[
                'ns-address-lookup__list-item',
                index < suggestions.length - 1 ? 'ns-address-lookup__list-item--divided' : ''
              ].filter(Boolean).join(' ')}
            >
              <button
                type="button"
                onClick={() => handleSelect(suggestion)}
                onKeyDown={(event) => handleSuggestionButtonKeyDown(event, index)}
                className="ns-address-lookup__list-button"
              >
                {suggestion.displayText}
              </button>
            </li>
          ))}
        </ul>
      );
    }

    if (shouldShowValidationResults && validationSuggestions.length > 0) {
      return (
        <ul className="ns-address-lookup__list ns-address-lookup__list--validation-inline">
          {validationSuggestions.map((suggestion, index) => (
            <li
              key={getValidationSuggestionKey(suggestion)}
              className={[
                'ns-address-lookup__list-item',
                index < validationSuggestions.length - 1 ? 'ns-address-lookup__list-item--divided' : ''
              ].filter(Boolean).join(' ')}
            >
              <button
                type="button"
                onClick={() => handleValidationSelect(suggestion)}
                onKeyDown={(event) => handleSuggestionButtonKeyDown(event, index)}
                className="ns-address-lookup__list-button"
              >
                <div className="ns-address-lookup__list-title">
                  {getValidationSummaryTitle(suggestion)}
                </div>
                <div className="ns-address-lookup__list-subtitle">
                  {suggestion.displayText}
                </div>
                <div className="ns-address-lookup__list-subtitle">
                  {getValidationSummaryMessage(suggestion)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      );
    }

    if (shouldShowNoResults) {
      return (
        <div className="ns-address-lookup__message">
          {fieldLabels?.usNoResults}
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (
      ['autocomplete', 'map'].includes(validationTriggerSource) &&
      validationSuggestions.length === 1 &&
      autoApplyValidationStartedRef.current
    ) {
      handleValidationSelect(validationSuggestions[0]);
    }

    if (
      validationTriggerSource === 'initial' &&
      autoApplyValidationStartedRef.current &&
      !validationLoading
    ) {
      const bestSuggestion = getBestInitialValidationResult(validationSuggestions);

      if (bestSuggestion) {
        handleValidationSelect(bestSuggestion);
        return;
      }

      autoApplyValidationStartedRef.current = false;
      setShowValidationSuggestions(false);
      setValidationTriggerSource(null);
    }
  }, [validationLoading, validationSuggestions, validationTriggerSource]);

  const renderField = (fieldConfig) => {
    const isLookupField = fieldConfig.field === lookupField;
    const fieldId = getFieldId(fieldConfig.field);
    const fieldLabel = areLabelsLoading ? '' : (fieldLabels?.[fieldConfig.field] || fieldConfig.label);
    const fieldError = getFieldErrorMessage(fieldConfig.field);
    const isInvalid = hasVisibleFieldError(fieldConfig.field);
    const isReadOnly = readOnlyView || ((!fieldConfig.editable && !isLookupField) && isAutocompleteMode);
    const fieldWidth = fieldConfig.width === 'full' ?
      '100%' :
      (fieldConfig.width === 'third' ? '33.333333%' : '50%');
    const inputClassName = [
      'ns-address-lookup__input',
      isReadOnly ? 'ns-address-lookup__input--readonly' : '',
      isLookupField && loading ? 'ns-address-lookup__input--loading' : ''
    ].filter(Boolean).join(' ');
    const labelRowClassName = [
      'ns-address-lookup__label-row',
      isLookupField ? '' : 'ns-address-lookup__label-row--field'
    ].filter(Boolean).join(' ');

    return (
      <div
        key={getFieldKey(fieldConfig)}
        className="ns-address-lookup__field"
        style={{ width: fieldWidth }}
      >
        <div className={labelRowClassName}>
          <label
            htmlFor={fieldId}
            className="MuiFormLabel-root MuiFormLabel-colorPrimary css-1ef1ceb-MuiFormLabel-root"
          >
            {fieldLabel}
            {fieldConfig.required && !areLabelsLoading && (
              <span
                aria-hidden="true"
                className="MuiFormLabel-asterisk"
              >
                {' *'}
              </span>
            )}
          </label>
          {isLookupField && !readOnlyView && renderLookupActions()}
        </div>
        <NsInputField
          id={fieldId}
          type="text"
          value={formData[fieldConfig.field] || ''}
          aria-invalid={shouldShowFieldError(fieldConfig.field) ? 'true' : undefined}
          fieldState={isInvalid ? 'error' : undefined}
          helperText={fieldError || undefined}
          required={fieldConfig.required}
          onBlur={() => handleFieldBlur(fieldConfig.field)}
          onKeyDown={isLookupField && !readOnlyView ? handleLookupInputKeyDown : undefined}
          onChange={(e) => {
            const filtered = filterAllowedCharacters(e.target.value, fieldConfig.allowedCharacters);
            if (readOnlyView) {
              return;
            }
            if (isLookupField && isAutocompleteMode) {
              handleInputChange({ ...e, target: { ...e.target, value: filtered } });
            } else {
              handleEditableFieldChange(fieldConfig.field, filtered);
            }
          }}
          InputProps={{
            readOnly: isReadOnly
          }}
          inputProps={{
            maxLength: fieldConfig.maxLength,
            'data-testid': getFieldTestId(fieldConfig.field)
          }}
          className={inputClassName}
        />
        {renderLookupLists(isLookupField)}
      </div>
    );
  };

  const renderAddressForm = () => {
    const config = resolvedAddressForm?.addressElements;

    if (!config?.length) {
      if (isFormLoading) {
        return <div>{fieldLabels?.loadingAddressForm}</div>;
      }

      return <div>No form available for this country: US</div>;
    }

    return (
      <>
        {config.map(renderField)}
      </>
    );
  };

  return (
    <div ref={componentRef} className="ns-address-lookup__panel">
      {lookupServiceError && (
        <div className="ns-address-lookup__message">{lookupErrorMessage}</div>
      )}
      {allowValidation && validationServiceError && (
        <div className="ns-address-lookup__message">
          {validationErrorMessage}
        </div>
      )}
      <div className="ns-address-lookup__form">
        {renderAddressForm()}
      </div>
      {showMapModal && (
        <div
          className="ns-address-lookup__modal-backdrop"
          onClick={() => setShowMapModal(false)}
        >
          <div
            className="ns-address-lookup__modal"
            role="dialog"
            aria-modal="true"
            aria-label={fieldLabels?.addressLocation}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ns-address-lookup__modal-header">
              <div className="ns-address-lookup__modal-title">
                {fieldLabels?.addressLocation}
              </div>
              <button
                type="button"
                className="ns-address-lookup__toggle"
                aria-label="Close"
                onClick={() => setShowMapModal(false)}
              >
                X
              </button>
            </div>
            <div ref={mapContainerRef} className="ns-address-lookup__map" />
          </div>
        </div>
      )}
    </div>
  );
};

SmartyUSResults.propTypes = {
  onSelect: PropTypes.func,
  initialAddress: addressPropType,
  requireValidation: PropTypes.bool,
  readOnlyView: PropTypes.bool,
  showManualValidateButton: PropTypes.bool,
  validationRequestId: PropTypes.number,
  language: PropTypes.string,
  strings: labelStringsPropType,
  formDefinition: formDefinitionPropType,
  deferFormLookup: PropTypes.bool
};

export default SmartyUSResults;
